import { OUTPUT_CHANNELS, OUTPUT_SAMPLE_RATE } from './constants';

/** Glues decoded chunks of one clip into one buffer (they are contiguous, so order is enough). */
export function concatAudioBuffers(buffers: readonly AudioBuffer[]): AudioBuffer | null {
  if (buffers.length === 0) {
    return null;
  }
  const { sampleRate, numberOfChannels } = buffers[0];
  const length = buffers.reduce((sum, buffer) => sum + buffer.length, 0);
  const result = new AudioBuffer({ length, sampleRate, numberOfChannels });
  for (let channel = 0; channel < numberOfChannels; channel++) {
    const target = result.getChannelData(channel);
    let offset = 0;
    for (const buffer of buffers) {
      // A chunk with fewer channels (rare, broken stream) repeats its last channel.
      target.set(buffer.getChannelData(Math.min(channel, buffer.numberOfChannels - 1)), offset);
      offset += buffer.length;
    }
  }
  return result;
}

/**
 * Brings a clip's audio to the output format and to exactly `durationSec`: resampled and up/down-mixed by
 * `OfflineAudioContext` (the Web Audio mixing rules), cut or padded with silence at the end.
 */
export async function normalizeAudio(buffer: AudioBuffer | null, durationSec: number): Promise<AudioBuffer> {
  const length = Math.max(1, Math.round(durationSec * OUTPUT_SAMPLE_RATE));
  const context = new OfflineAudioContext({
    numberOfChannels: OUTPUT_CHANNELS,
    sampleRate: OUTPUT_SAMPLE_RATE,
    length,
  });
  if (buffer) {
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    source.start(0);
  }
  return context.startRendering();
}
